import React, { FC } from 'react'
import { TToastNewState } from 'transfix-ui/components/Toast'
import { Box, Divider, Typography } from 'transfix-ui/core'
import { ResponsiveLayout } from 'apps/fleet-planner/components/layout/ResponsiveLayout'
import { ResponsiveLayoutRow } from 'apps/fleet-planner/components/layout/ResponsiveLayoutRow'
import { NOTIFICATION_TIMES } from 'apps/fleet-planner/components/user/NotificationsCard'
import { useUserContext } from 'apps/fleet-planner/context/UserContext'
import { Checkbox, Switch } from 'transfix-ui'

interface IEmailPreferencesMobilePageProps {
  /**
   * A function provided by the parent component to navigate to the default page
   */
  onGoToDefaultRoute: () => void

  /**
   * A function provided by the parent component to open the toast on the main page
   */
  onOpenToast: (options: TToastNewState) => void
}

export const EmailPreferencesMobilePage: FC<IEmailPreferencesMobilePageProps> = ({
  onGoToDefaultRoute,
  onOpenToast,
}) => {
  const { errors, updateUserSettings, userData } = useUserContext()
  const { user } = userData || {}
  const { email, settings } = user || {}
  const { loadRecommendationsDigest = [] } = settings || {}
  const isLoadRecommendationsDigestEnabled = !!loadRecommendationsDigest.length

  const pressBackButtonHandler = () => {
    onGoToDefaultRoute()
  }

  const updateSettings = async (loadRecommendationsDigest: number[]) => {
    try {
      await updateUserSettings({
        settings: {
          loadRecommendationsDigest,
        },
      })
      onOpenToast({
        toastType: errors ? 'alert' : 'default',
        toastMessage: errors || 'Changes Saved.',
      })
    } catch (error) {
      onOpenToast({
        toastType: 'alert',
        toastMessage: 'Something went wrong.',
      })
    }
  }

  const switchChangeHandler = () => {
    const allNotificationTimes = NOTIFICATION_TIMES.map(({ value }) => value)

    // when toggling ON: select all times - when toggling OFF: remove all times
    const payload = isLoadRecommendationsDigestEnabled ? [] : allNotificationTimes

    updateSettings(payload)
  }

  const checkboxChangeHandler = (value: number) => {
    const payload = [...loadRecommendationsDigest]

    // toggling the `value`, considering if it "is" or it "is not" present on the control array
    if (!loadRecommendationsDigest.includes(value)) {
      payload.push(value)
    } else {
      payload.splice(payload.indexOf(value), 1)
    }

    updateSettings(payload)
  }

  return (
    <ResponsiveLayout title='Email Preferences' onPressBackButton={pressBackButtonHandler}>
      <ResponsiveLayoutRow>
        <Typography variant='body1'>
          We&apos;ll send the selected emails to <span className='fs-mask'>{email}</span>
        </Typography>

        {/* Toggle daily recommendations */}
        <Box pt={5}>
          <Typography variant='subtitle1'>Daily ‘Fix Load Recommendations Email</Typography>

          <Typography variant='body2'>
            A digest of load recommendations that is sent up to {NOTIFICATION_TIMES.length} times
            per day
          </Typography>

          {/* NOTE: `pl={2}` here shouldn't be needed, but Switch (FormControlLabel) has a marginLeft that I couldn't remove without the fear of breaking something else */}
          <Box pt={1} pb={1} pl={2}>
            <Switch
              label='On'
              onChange={switchChangeHandler}
              checked={isLoadRecommendationsDigestEnabled}
            />
          </Box>
        </Box>

        <Box pt={1} pb={1}>
          <Divider />
        </Box>

        {/* Notification times options */}
        <Box display='flex' flexDirection='row' pt={1}>
          {NOTIFICATION_TIMES.map(notificationTime => {
            const { label, value } = notificationTime || {}
            const changeHandler = () => {
              checkboxChangeHandler(value)
            }
            return (
              <Box key={`notification-time-${label}`} pr={2}>
                <Checkbox
                  onChange={changeHandler}
                  label={label}
                  checked={loadRecommendationsDigest.includes(value)}
                />
              </Box>
            )
          })}
        </Box>
      </ResponsiveLayoutRow>
    </ResponsiveLayout>
  )
}
